// Ongevallen (DFD / K781) product constants — values sourced from the BFF product
// schema products/ongevallen.json (branche 011, AFD definitie 011-K781-DFD
// Ongevallenverzekering, coverage section OD), EXCEPT ONGEVALLEN_MYAAND below.
//
// The carrier for ongevallen must stay with value K781. The ADN definition is under that name, otherwise, the calculation stops working. The "P413" is carried to is carried by PP_MYAAN and PP_MYNAAM for anva label 10001.
export const ONGEVALLEN_CARRIER = 'K781';
export const ONGEVALLEN_MYAAND = 'P413';

export const ONGEVALLEN_PRODUCT = {
  carrier: ONGEVALLEN_CARRIER,
  productName: 'DFD Ongevallenverzekering',
  calcMethod: `api/v1/Products/Calculate/${ONGEVALLEN_CARRIER}/DFD Ongevallenverzekering/accept.new`,
} as const;

// ANVA-registered voorwaarden code (poliscodelabel 10698). The engine itself emits the
// unregistered spelling SAA_DFDDON.2025.01 — see normalizeOngevallenVwCodes in calc.ts.
export const ONGEVALLEN_VW_CODE = 'SAA.DFDON.2025.01';

export const ONGEVALLEN_DEFAULTS: Record<string, string | number> = {
  OD_CODE: '1015', // schema default: soort dekking (ongevallen)
  OD_ENTREF: '1015', // schema default: entiteitsreferentie
  OD_PPRC: 20,
  OD_VOLGNUM: 1,
  OD_COMBINA: '1', // fallback for records saved before the sums were separate fields (calc.ts)
  OD_COMBIKN: '1', // children's fallback, same backwards compatibility
  // VZ_BERMIL deliberately NOT defaulted: required question, the user must answer it.
  VE_MYAAND: ONGEVALLEN_CARRIER,
  PP_MYAAND: ONGEVALLEN_MYAAND,
  PP_MYNAAM: ONGEVALLEN_MYAAND,
  PP_BRANCHE: '011',
  PP_AFDDEFN: `011-${ONGEVALLEN_CARRIER}-DFD Ongevallenverzekering`,
  PP_ADEFVRS: '001.00',
  PP_AFDDTAG: 'SUIV',
  PP_AFWAFSP: 'N',
  PP_CDUUMND: 12,
  PP_POLPKJN: 'J', // polis onderdeel van het pakket
  PP_STPRV: 'J',
  PP_HANDACC: 'N',
  PP_SLOTAKK: 'N',
  PP_BTWCD: 'E',
  PP_BETTERM: 1,
};

// Request/enrich-path PP defaults (final documents only). Deliberately NOT part of
// ONGEVALLEN_DEFAULTS: buildDfdDocument spreads that map into every calculate payload,
// which is golden-baselined and must not silently gain fields. Both wire touchpoints
// (summary/enrich.ts and ConfirmationScreen) apply this same record via applyPpDefaults.
export const ONGEVALLEN_PP_REQUEST_DEFAULTS: Record<string, string | number> = {
  PP_SPECLIM: 'N',
  PP_CDUUMND: ONGEVALLEN_DEFAULTS.PP_CDUUMND,
  PP_BTWCD: ONGEVALLEN_DEFAULTS.PP_BTWCD,
};

export const ONGEVALLEN_REQUIRED_FIELDS = [
  'OD_GSS', // Gezinssamenstelling
  'OD_OVERL', // Verzekerd bedrag overlijden
  'OD_INVALID', // Verzekerd bedrag blijvende invaliditeit
  'VZ_BERMIL', // Beroepsmilitair (VZ_BERMILP is required too, but only with a partner GSS)
] as const;

export const hiddenOngevallenFields = (config: unknown): ReadonlySet<string> => {
  const list = (config as any)?.ongevallen?.hiddenFields;
  return new Set(Array.isArray(list) ? list.map(String) : []);
};

export const ongevallenMissingRequired = (
  vals: Record<string, string | number | undefined>,
  hidden: ReadonlySet<string>,
): boolean => {
  const missingBase = ONGEVALLEN_REQUIRED_FIELDS.some((k) => {
    if (hidden.has(k)) return false;
    const v = vals[k];
    return v == null || v === '';
  });
  const partnerGss = (GSS_WITH_PARTNER as readonly string[]).includes(String(vals.OD_GSS ?? ''));
  const partnerMissing = partnerGss && !hidden.has('VZ_BERMILP')
    && (vals.VZ_BERMILP == null || vals.VZ_BERMILP === '');
  return missingBase || partnerMissing;
};

export interface SelectOption {
  value: string | number;
  labelKey?: string;
  label: string;
}

export interface FieldDef {
  key: string;
  labelKey: string;
  fallbackLabel: string;
  options: SelectOption[];
}

// Same shared question as every product (options mirror common.familyComposition.*).
export const FAMILY_COMPOSITION_FIELD: FieldDef = {
  key: 'OD_GSS',
  labelKey: 'common.personalInfo.familyComposition',
  fallbackLabel: 'Gezinssamenstelling',
  options: [
    { value: 'A', labelKey: 'common.familyComposition.A', label: 'Alleenstaande zonder kind(eren)' },
    { value: 'H', labelKey: 'common.familyComposition.H', label: 'Gezin zonder kind(eren)' },
    { value: 'I', labelKey: 'common.familyComposition.I', label: 'Gezin met kind(eren)' },
    { value: 'K', labelKey: 'common.familyComposition.K', label: 'Alleenstaande met kind(eren)' },
  ],
};

export interface CombinationSums {
  code: '1' | '2' | '3' | '4' | '5' | '6';
  overl: number;
  invalid: number;
}

export const COMBINATIONS: readonly CombinationSums[] = [
  { code: '1', overl: 10000, invalid: 100000 },
  { code: '2', overl: 10000, invalid: 150000 },
  { code: '3', overl: 10000, invalid: 200000 },
  { code: '4', overl: 100000, invalid: 100000 },
  { code: '5', overl: 150000, invalid: 150000 },
  { code: '6', overl: 200000, invalid: 200000 },
] as const;

/** Sums for a combination code; unknown codes fall back to combination 1. */
export const sumsForCombination = (code: string | number | undefined): CombinationSums =>
  COMBINATIONS.find((c) => c.code === String(code)) ?? COMBINATIONS[0];

/**
 * Resolve a user-selected (overlijden, invaliditeit) pair back to the engine's
 * combination code. Null = the pair doesn't exist in the pricing table (the
 * screen shows a validation message instead of a price).
 */
export const combinationForSums = (
  overl: string | number | undefined,
  invalid: string | number | undefined,
): CombinationSums | null =>
  COMBINATIONS.find((c) => c.overl === Number(overl) && c.invalid === Number(invalid)) ?? null;

/** First combination whose invaliditeit sum matches (best-effort code for a pair the table doesn't know). */
export const combinationForInvalid = (invalid: string | number | undefined): CombinationSums | null =>
  COMBINATIONS.find((c) => c.invalid === Number(invalid)) ?? null;

// Both option lists derive from COMBINATIONS (the pricing table) so the form can only
// offer amounts that exist in at least one combination.
export const DEATH_SUM_OPTIONS: readonly number[] = [...new Set(COMBINATIONS.map((c) => c.overl))];
export const INVALID_SUM_OPTIONS: readonly number[] = [...new Set(COMBINATIONS.map((c) => c.invalid))];

/**
 * Invaliditeit sums that form a valid combination with the chosen overlijden sum.
 * Overlijden 10.000 leaves all three open (combi 1-3); the higher overlijden amounts
 * only pair with themselves (combi 4-6), so the screen locks the invaliditeit field
 * to that single value.
 */
export const invalidSumsForOverl = (overl: string | number | undefined): readonly number[] =>
  COMBINATIONS.filter((c) => c.overl === Number(overl)).map((c) => c.invalid);

export const OVERL_FIELD = {
  key: 'OD_OVERL',
  labelKey: 'screens.ongevallen.fields.overl',
  fallbackLabel: 'Verzekerd bedrag overlijden',
} as const;

export const INVALID_FIELD = {
  key: 'OD_INVALID',
  labelKey: 'screens.ongevallen.fields.invalid',
  fallbackLabel: 'Verzekerd bedrag blijvende invaliditeit',
} as const;

export const GSS_WITH_CHILDREN = ['I', 'K'] as const;
export const GSS_WITH_PARTNER = ['H', 'I'] as const;

// Children's combinations — engine-verified pairs (the OD_OVERLKND codelist surfaced by
// the engine: 10000/15000/25000; independent of the adult combination). OD_OVERLKND is
// absent from the schema (hidden in the original product UI).
export const KIDS_COMBINATIONS: readonly CombinationSums[] = [
  { code: '1', overl: 10000, invalid: 100000 },
  { code: '2', overl: 15000, invalid: 150000 },
  { code: '3', overl: 25000, invalid: 200000 },
] as const;

/** Kids sums for a combination code; unknown codes fall back to combination 1. */
export const kidsSumsForCombination = (code: string | number | undefined): CombinationSums =>
  KIDS_COMBINATIONS.find((c) => c.code === String(code)) ?? KIDS_COMBINATIONS[0];

/**
 * Resolve the children's invaliditeit sum back to its combination. OD_OVERLKND is
 * hidden in the original product UI and strictly coupled to the invaliditeit sum,
 * so the single kids dropdown determines the whole pair.
 */
export const kidsCombinationForInvalid = (
  invalid: string | number | undefined,
): CombinationSums | null =>
  KIDS_COMBINATIONS.find((c) => c.invalid === Number(invalid)) ?? null;

export const KIDS_INVALID_OPTIONS: readonly number[] = KIDS_COMBINATIONS.map((c) => c.invalid);

// Only for a gezinssamenstelling with kind(eren) (GSS I or K).
export const KIDS_INVALID_FIELD = {
  key: 'OD_VBBIKND',
  labelKey: 'screens.ongevallen.fields.vbbiknd',
  fallbackLabel: 'Verzekerd bedrag invaliditeit kinderen',
} as const;

export const BERMIL_FIELD: FieldDef = {
  key: 'VZ_BERMIL',
  labelKey: 'screens.ongevallen.fields.bermil',
  fallbackLabel: 'Beroepsmilitair',
  options: [
    { value: 'J', labelKey: 'common.yes', label: 'Ja' },
    { value: 'N', labelKey: 'common.no', label: 'Nee' },
  ],
};

// Partner beroepsmilitair — only asked (and only sent, see calc.ts) when the
// gezinssamenstelling includes a partner (GSS H or I).
export const BERMILP_FIELD: FieldDef = {
  key: 'VZ_BERMILP',
  labelKey: 'screens.ongevallen.fields.bermilp',
  fallbackLabel: 'Partner beroepsmilitair',
  options: [
    { value: 'J', labelKey: 'common.yes', label: 'Ja' },
    { value: 'N', labelKey: 'common.no', label: 'Nee' },
  ],
};
